import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import api, { fileUrl } from "@/lib/api";
import { rupee, effectivePrice } from "@/lib/format";
import { ShoppingBasket, Plus, Minus, Star } from "lucide-react";

export function GroceryCard({ product }) {
  const { items, addItem, updateQty } = useCart();
  const img = fileUrl(product.image_id);
  const inCart = items.find((i) => i.id === product.id);
  const qty = inCart?.quantity || 0;
  const hasDiscount = product.discount_price && product.discount_price > 0;
  const soldOut = product.stock <= 0;
  const pct = hasDiscount ? Math.round(((product.price - product.discount_price) / product.price) * 100) : 0;

  const track = () => api.post(`/products/${product.id}/click`).catch(() => {});
  const add = () => { if (soldOut) return; track(); addItem(product, 1); };
  const inc = () => updateQty(product.id, qty + 1);
  const dec = () => updateQty(product.id, qty - 1);

  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-white transition-shadow hover:shadow-lift" data-testid={`grocery-card-${product.id}`}>
      <Link to={`/products/${product.id}`} className="relative block">
        <div className="aspect-square w-full overflow-hidden bg-cream-alt">
          {img ? (
            <img src={img} alt={product.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-ink-muted"><ShoppingBasket className="h-10 w-10" /></div>
          )}
        </div>
        {product.is_bestseller && !soldOut && (
          <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-navy px-2 py-0.5 text-[10px] font-bold text-white"><Star className="h-2.5 w-2.5 fill-brand text-brand" /> Bestseller</span>
        )}
        {hasDiscount && !soldOut && (
          <span className="absolute right-2 top-2 rounded-md bg-brand px-1.5 py-0.5 text-[10px] font-bold text-ink">{pct}% OFF</span>
        )}
        {soldOut && (
          <span className="absolute inset-0 flex items-center justify-center bg-white/70 text-xs font-semibold uppercase tracking-widest text-ink">Sold Out</span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-2.5">
        <span className="text-[11px] font-medium text-ink-muted">{product.unit}</span>
        <Link to={`/products/${product.id}`}>
          <h3 className="mt-0.5 text-sm font-semibold leading-snug text-ink line-clamp-2 hover:text-brand">{product.name}</h3>
        </Link>
        {product.seller_name && <p className="mt-0.5 truncate text-[10px] text-ink-muted">{product.seller_name}</p>}

        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div className="min-w-0">
            <div className="text-sm font-bold text-ink">{rupee(effectivePrice(product))}</div>
            {hasDiscount && <div className="text-[11px] text-ink-muted line-through">{rupee(product.price)}</div>}
          </div>
          {soldOut ? (
            <span className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-ink-muted">Out</span>
          ) : qty === 0 ? (
            <button onClick={add} data-testid={`grocery-add-${product.id}`} className="rounded-lg border border-brand bg-brand/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-ink transition-colors hover:bg-brand">ADD</button>
          ) : (
            <div className="flex items-center rounded-lg bg-brand text-ink" data-testid={`grocery-stepper-${product.id}`}>
              <button onClick={dec} className="flex h-8 w-7 items-center justify-center" data-testid={`grocery-dec-${product.id}`} aria-label="Decrease"><Minus className="h-3.5 w-3.5" /></button>
              <span className="w-5 text-center text-sm font-bold" data-testid={`grocery-qty-${product.id}`}>{qty}</span>
              <button onClick={inc} className="flex h-8 w-7 items-center justify-center" data-testid={`grocery-inc-${product.id}`} aria-label="Increase"><Plus className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
